import { useEffect, useRef, useState } from "react"
import { Input as AntInput } from "antd"
import type { InputRef } from "antd"
import { useProjectNumber } from "hooks/useProjectNumber.hook"
import { EditIcon } from "components/ui/icons/edit/EditIcon"
import type { TOnChangeInput, TOnKeyDown } from "types/default.type"
import style from "./ProjectNumber.module.scss"

const { wrapper, trigger, placeholder, value, editIcon, input } = style

export const PROJECT_NUMBER_PLACEHOLDER = "Add project number"

type Props = {
	className?: string
	// When false (non-owner), the value is shown read-only and nothing renders while unset.
	editable?: boolean
}

/**
 * Inline project-number control shown next to the drawing name in the drawer header (REQ-379).
 * Renders a muted "Add project number" placeholder when unset; clicking the placeholder or the value
 * switches to an inline input. Enter or blur confirms; Escape cancels. Self-contained — it reads/writes
 * the shared value through `useProjectNumber`, so it can be dropped into any drawer header (Documents,
 * Quotations, …) without wiring. No submission/status gating; editing is limited to the file owner.
 */
export const ProjectNumber = ({ className, editable = true }: Props) => {
	const { projectNumber, isSet, isLoading, setProjectNumber } = useProjectNumber()
	const [isEditing, setIsEditing] = useState(false)
	const [draft, setDraft] = useState(projectNumber)
	const inputRef = useRef<InputRef>(null)
	// Set right before a programmatic blur triggered by Escape, so the shared onBlur handler knows to
	// discard instead of commit (blur is the single commit path for Enter / click-away alike).
	const skipCommitRef = useRef(false)

	// Keep the draft aligned with the stored value while in view mode (e.g. an external update lands).
	useEffect(() => {
		if (!isEditing) setDraft(projectNumber)
	}, [projectNumber, isEditing])

	const startEditing = () => {
		setDraft(projectNumber)
		setIsEditing(true)
	}

	const handleBlur = () => {
		if (skipCommitRef.current) {
			skipCommitRef.current = false
			setDraft(projectNumber)
			setIsEditing(false)
			return
		}
		// Fire-and-forget: setProjectNumber handles its own optimistic update, reconcile and error toast.
		void setProjectNumber(draft)
		setIsEditing(false)
	}

	const onKeyDown = (e: TOnKeyDown) => {
		if (e.key === "Escape") {
			e.stopPropagation()
			skipCommitRef.current = true
			inputRef.current?.blur()
		}
	}

	if (isLoading) return null

	// Non-owner: show the value read-only; render nothing when there is no value to show.
	if (!editable) {
		if (!isSet) return null
		return (
			<div className={className ? `${wrapper} ${className}` : wrapper} data-testid="project-number">
				<span className={value}>{projectNumber}</span>
			</div>
		)
	}

	return (
		<div className={className ? `${wrapper} ${className}` : wrapper} data-testid="project-number">
			{isEditing ? (
				<AntInput
					ref={inputRef}
					autoFocus
					className={input}
					value={draft}
					placeholder={PROJECT_NUMBER_PLACEHOLDER}
					variant="borderless"
					onChange={(e: TOnChangeInput) => setDraft(e.target.value)}
					onPressEnter={() => inputRef.current?.blur()}
					onBlur={handleBlur}
					onKeyDown={onKeyDown}
				/>
			) : (
				<button type="button" className={trigger} onClick={startEditing}>
					<span className={isSet ? value : placeholder}>{isSet ? projectNumber : PROJECT_NUMBER_PLACEHOLDER}</span>
					{isSet ? <EditIcon className={editIcon} /> : null}
				</button>
			)}
		</div>
	)
}
